import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  MoreVert,
  Payments,
  CalendarToday,
  AccessTime,
  VerifiedUser,
  Delete,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import SessionFormDialog from '../components/SessionFormDialog';
import SessionSeatMap from '../components/seat-layout/SessionSeatMap';
import { useMyLocations, seatSelectionAvailable } from '../hooks/useMyLocations';
import { isStudioScopedRole } from '../utils/roles';

interface Session {
  _id: string;
  className: string;
  date: string;
  price: number;
  slots: number;
  duration: number;
  image: string;
  description: string;
  payAtStudio: boolean;
  isLive: boolean;
  onlyForFemales: boolean;
  instructor?: {
    name: string;
    image: string;
  };
  classType?: {
    title: string;
  };
  studio?: {
    studioName: string;
    images: string[];
  };
}

const SessionDetailPage: React.FC = () => {
  // Whether this account has the seat-selection feature at all — the same
  // condition the nav uses for "Rooms & Layouts". See seatSelectionAvailable.
  const { locations: myLocations } = useMyLocations();
  // Instructors hold `classes.view`, which is what this route requires, so an
  // instructor the studio has NOT granted session management could open a
  // session here and was still shown Edit and Delete.
  const { canWriteSessions, admin } = useAuth();
  // A platform admin always sees it, as before — the check is for studios
  // that have no Rooms page to act on the "attach a room" note.
  const canUseSeats =
    !isStudioScopedRole(admin?.role) || seatSelectionAvailable(admin, myLocations);
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSessionDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getSessionById(sessionId!);
      setSession(response.session);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch session details');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetails();
    }
  }, [sessionId, fetchSessionDetails]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    handleMenuClose();
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      // Pinned to the session's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      await apiService.deleteSession(sessionId!, studioIdOf(session?.studio));
      navigate(-1);
    } catch (err: any) {
      setError(err.message || 'Failed to delete session');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !session) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Session not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            {session.image && (
              <Avatar
                src={session.image}
                variant="rounded"
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                }}
              />
            )}
            <Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h4" fontWeight={700}>
                  {session.className}
                </Typography>
                {session.classType && (
                  <Chip 
                    label={session.classType.title} 
                    size="small" 
                    sx={{ backgroundColor: colors.backgroundTertiary, border: `1px solid ${colors.divider}` }} 
                  />
                )}
                <Chip
                  label={session.isLive ? 'Live' : 'Cancelled'}
                  size="small"
                  color={session.isLive ? 'success' : 'error'}
                />
                {session.onlyForFemales && (
                  <Chip
                    label="Female Only"
                    size="small"
                    sx={{ backgroundColor: `${colors.secondary}15`, color: colors.secondary }}
                  />
                )}
              </Box>
              {session.studio && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {session.studio.studioName}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Every control in this cluster writes the session, so the whole
              group shares one gate — nothing here is read-only. */}
          {canWriteSessions && (
            <Box display="flex" gap={1}>
              <Button variant="outlined" size="small" startIcon={<Edit />} onClick={handleEditClick}>
                Edit
              </Button>
              <IconButton size="small" onClick={handleMenuClick}>
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleEditClick}>
                  <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                  <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Left Column - Details */}
        <Box>
          {/* Image - Compact */}
          {session.image && (
            <Card sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  component="img"
                  src={session.image}
                  alt={session.className}
                  sx={{
                    width: '100%',
                    height: { xs: 150, sm: 200 },
                    objectFit: 'cover',
                    borderRadius: 2,
                  }}
                />
              </CardContent>
            </Card>
          )}

          {/* Description - Compact */}
          {session.description && (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  About This Class
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>
                  {session.description}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Right Column - Info */}
        <Box>
          {/* Session Info Card */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Session Details
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={1.5}>
                {/* Date & Time */}
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <CalendarToday sx={{ fontSize: 18, color: colors.textSecondary, mt: 0.5 }} />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date & Time
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(session.date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        timeZone: 'Asia/Kolkata'
                      })}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(session.date).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Asia/Kolkata'
                      })}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                {/* Duration */}
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <AccessTime sx={{ fontSize: 18, color: colors.textSecondary, mt: 0.5 }} />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Duration
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {session.duration} minutes
                    </Typography>
                  </Box>
                </Box>

                {session.instructor && (
                  <>
                    <Divider />
                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                      <VerifiedUser sx={{ fontSize: 18, color: colors.textSecondary, mt: 0.5 }} />
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Instructor
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {session.instructor.name}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                <Divider />

                {/* Payment */}
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <Payments sx={{ fontSize: 18, color: colors.textSecondary, mt: 0.5 }} />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Price
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ₹{session.price?.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {session.payAtStudio ? 'Pay at studio' : 'Pay online'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Live seat map.
              ── WHY THIS IS GATED ──────────────────────────────────────────
              It used to render unconditionally, on the reasoning that a
              session with no room shows only an explanatory note. But that
              note reads "Attach a room to its class to let members pick a
              bike" — an instruction pointing at the Rooms & Layouts page,
              which the navigation HIDES unless seat selection is enabled
              (Layout.tsx). So every studio without the feature was shown a
              permanent notice telling them to do something they have no
              screen to do, about a feature they do not have.
              Same condition as the nav, via one shared helper so the two
              cannot drift apart. */}
          {canUseSeats && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Seat map
                </Typography>
                <SessionSeatMap sessionId={session._id} autoRefreshMs={10000} />
              </CardContent>
            </Card>
          )}

          {/* Availability Card */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Availability
              </Typography>
              
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Slots
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color={colors.textPrimary}>
                    {session.slots}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={session.isLive ? 'Live' : 'Cancelled'}
                    size="small"
                    color={session.isLive ? 'success' : 'error'}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Edit Dialog */}
      <SessionFormDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={fetchSessionDetails}
        session={session}
      />

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this session? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SessionDetailPage;

